import React, { useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  LocationOn,
  Edit,
  Delete,
  CalendarToday,
  TrackChanges,
  TrendingDown,
} from "@mui/icons-material";
import { Organization } from "./types";
import Organization_2 from "../../assets/Images/client/Organization_2.png";
import AlertDialog from "components/AlertDialog";
import { ConfirmationMessages } from "constants/constant";
import { _deleteOrganization } from "middlewares/OrganizationApi/organization";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import SnackBar from "components/SnackBar";
import OrganizationReductionMeasureDialog from "./OrganizationReductionMeasureDialog";

interface OrganizationCardsProps {
  organizations: Organization[];
  onOrganizationClick?: (organization: Organization) => void;
  onOrganizationDeleted?: (orgId: number) => void; // Add this prop
  onEditOrganizationClick?: (organization: Organization) => void;
  onSetTargetClick?: (organization: Organization) => void;
}

const OrganizationCards: React.FC<OrganizationCardsProps> = ({
  organizations,
  onOrganizationClick,
    onOrganizationDeleted, // Destructure the new prop
    onEditOrganizationClick,
    onSetTargetClick,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [selectedOrgName, setSelectedOrgName] = useState<string>("");
  const [reductionMeasureOrg, setReductionMeasureOrg] = useState<Organization | null>(null);

  const formatDate = (date?: Date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleEdit = (organization: Organization) => {
    onEditOrganizationClick?.(organization);
  };

  const handleSetTarget = (organization: Organization) => {
    onSetTargetClick?.(organization);
  };

  const handleDelete = (orgId: number, orgName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOrgId(orgId);
    setSelectedOrgName(orgName);
    openDialog();
  };

  const openDialog = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setSelectedOrgId(null);
    setSelectedOrgName("");
  };

  const deleteOrganization = async () => {
    if (selectedOrgId === null) return;

    try {
      const res = await _deleteOrganization(selectedOrgId);
      if (res) {
        onOrganizationDeleted?.(selectedOrgId);

        // Show success message
        handleCloseSnackBar({
          show: true,
          severity: "success",
          message: "Deleted Successfully!",
        });
      }
    } catch (err) {
      console.error("Error:", err);
      // Show error message
      handleCloseSnackBar({
        show: true,
        severity: "error",
        message: "Failed to delete organization",
      });
    }
  };

  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  const handleConfirm = async () => {
    await deleteOrganization(); // Wait for deletion to complete
    handleClose();
  };

  return (
    <>
      <Grid container spacing={2}>
        {organizations.map((org) => (
          <Grid item xs={12} sm={6} md={2.4} key={org.orgId}>
            <Card
              onClick={() => onOrganizationClick?.(org)}
              sx={{
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                transition: "transform 0.2s, box-shadow 0.2s",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              <CardMedia
                component="img"
                height="70"
                image={org.imageName && org.imageName.trim() !== "" ? org.imageName : Organization_2}
                alt={org.orgName}
                sx={{ objectFit: "cover" }}
              />
              <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 0.75, fontSize: "0.875rem", lineHeight: 1.3 }}
                >
                  {org.orgName}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  <LocationOn sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    {org.locations.length} Location
                    {org.locations.length !== 1 ? "s" : ""}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarToday
                      sx={{ fontSize: 12, color: "text.secondary" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {formatDate(org.modifiedAt)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 0.75 }}>
                    <Tooltip title="Set Target">
                      <IconButton
                        size="small"
                        aria-label="Set Target"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetTarget(org);
                        }}
                        sx={{
                          padding: "2px",
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: "primary.light",
                            color: "primary.dark",
                          },
                        }}
                      >
                        <TrackChanges sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      aria-label="Edit organization"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(org);
                      }}
                      sx={{
                        padding: "2px",
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "primary.light",
                          color: "primary.dark",
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Delete organization"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(org.orgId, org.orgName, e);
                      }}
                      sx={{
                        padding: "2px",
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "error.light",
                          color: "error.main",
                        },
                      }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                    {/*
                    <Tooltip title="Reduction Measures">
                      <IconButton
                        size="small"
                        aria-label="Reduction Measures"
                        onClick={(e) => {
                          e.stopPropagation();
                          setReductionMeasureOrg(org);
                        }}
                        sx={{
                          padding: "2px",
                          color: "primary.main",
                          "&:hover": {
                            bgcolor: "primary.light",
                            color: "primary.dark",
                          },
                        }}
                      >
                        
                        <TrendingDown sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>*/}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <AlertDialog
        open={openModal}
        openDialog={openDialog}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        locationName={selectedOrgName}
        confirmationMessage={ConfirmationMessages.orgConfirmation}
      />
      <OrganizationReductionMeasureDialog
        open={reductionMeasureOrg !== null}
        organization={reductionMeasureOrg}
        onClose={() => setReductionMeasureOrg(null)}
        showSnackbar={setSuccess}
      />
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          setSuccess({ show: false, severity: "info", message: "" })
        }
      />
    </>
  );
};

export default OrganizationCards;